/** @format */

import { TableBody, TableCell, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Pager from '../../../components/Adescom/Pager';
import Loader from '../../../components/Loaders/Loader';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { store } from '../../../core/store';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import ReportHelper from '../../../helpers/ReportHelper';
import SpecializedReportTypeInterface from '../../../interfaces/platform/SpecializedReportTypeInterface';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { Auth } from '../../../modules/auth/Auth';
import { reportConstants } from '../../../modules/reports/actions';
import { specializedReportConstants, specializedReportReducers, specializedReportType } from '../../../modules/specializedReports/reducers';
import SpecializedReportForm from './SpecializedReportForm';
import CancelButton from '../../../components/Buttons/CancelButton';
import PaperedTable from '../../../components/Table/PaperedTable';
import ReportList from '../CSV/ReportList';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { NotificationHelper } from '../../../helpers/NotificationHelper';

store.injectReducer('specializedReportReducers', specializedReportReducers);

const SpecializedReport = (props: { classes: any }): React.ReactElement => {
    const dispatch = useDispatch();

    const ITEM_LOADER = new ItemLoaders(dispatch);

    const SPECIALIZED_REPORT_TYPE_URI = '/specialized_report_types';

    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [visibleLoader, setVisibleLoader] = useState<boolean>(false);
    const [activeCard, setActiveCard] = useState<number>(1);

    const { collections } = useSelector((state: { specializedReportReducers: specializedReportType }) => {
        return state.specializedReportReducers;
    });

    const tableStickyHeader = { root: props.classes.tableStickyHeader };
    const tableCellRoot = { root: props.classes.tableCellRoot };
    const tableStickyCommand = { root: props.classes.tableStickyCommand };

    /**  HELPER FUNCTIONS */

    const firstLoad = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(SPECIALIZED_REPORT_TYPE_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(FormGeneralHelper.getByUri(uri), specializedReportConstants.LIST, [], [], () => {}, {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            });
        },
        [dispatch]
    );

    const paginationCallbackFunction = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(SPECIALIZED_REPORT_TYPE_URI, configDefault.PLATFORM_API_PREFIX);
            }

            ITEM_LOADER.getListWithCallback(FormGeneralHelper.getByUri(uri), specializedReportConstants.LIST, filters, orders, () => {}, {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            });
        },
        [ITEM_LOADER, filters, orders]
    );

    const updateOrderList = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilterList = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    const generateReport = (params: any): void => {
        const data: { params: string; typeId: number; platformUserId: number } = {
            params: JSON.stringify({ ...params.entity, reportName: params.reportName }),
            typeId: params.typeId || ReportHelper.reportType.SPECIALIZED_REPORT,
            platformUserId: Auth.getUserId(),
        };
        if (params.formSettings && (params.formSettings.displayResellerSimpleSelect || params.formSettings.displayResellerMultiSelect)) {
            if (params.entity.platformResellerId.length === 0) {
                NotificationHelper.notifyError('Wybierz resellera', dispatch);
                return;
            }
        }
        setVisibleLoader(true);

        ITEM_LOADER.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/reports', configDefault.PLATFORM_API_PREFIX)),
            reportConstants.REPORT_ADD,
            data,
            {
                onSuccess: () => {
                    setVisibleLoader(false);
                },
                onError: () => {
                    setVisibleLoader(false);
                },
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const clearFilter = (): void => {
        setFilters([]);
        setOrders([]);
    };

    const swapActiveView = (param: { page: number }): void => {
        setActiveCard(param.page);
    };

    /**  EFFECTS */

    useEffect(() => {
        firstLoad();
    }, [firstLoad]);

    useEffect(() => {
        const loader = new ItemLoaders(dispatch);
        loader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(SPECIALIZED_REPORT_TYPE_URI, configDefault.PLATFORM_API_PREFIX)),
            specializedReportConstants.LIST,
            filters,
            orders,
            () => {},
            {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            }
        );
    }, [filters, orders, dispatch]);

    return (
        <GridContainer>
            <GridItem xs={12}>
                <CustomTextButtom
                    callback={swapActiveView}
                    callbackParam={{ page: 1 }}
                    color="primary"
                    label="specialized_report:b.report_list"
                    size="lg"
                />
                <CustomTextButtom
                    callback={swapActiveView}
                    callbackParam={{ page: 2 }}
                    color="primary"
                    label="specialized_report:b.generate_report"
                    size="lg"
                />
            </GridItem>
            <GridItem xs={12}>
                {activeCard === 1 && <ReportList />}
                {activeCard === 2 && (
                    <>
                        <SpecializedReportForm generateReport={generateReport} />
                        <PaperedTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell classes={tableStickyCommand} align="left">
                                        <TableHeaderContentCell textAlign="left" title="clients:c.commands" />
                                        {(orders.length > 0 || filters.length > 0) && (
                                            <CancelButton callback={clearFilter} callbackParam={{}} tooltipTitle="general:common.clear_filter" />
                                        )}
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} align="left">
                                        <TableHeaderContentCell
                                            textAlign="left"
                                            title="specialized_report:h.name"
                                            updateOrderList={updateOrderList}
                                            updateFilterList={updateFilterList}
                                            fieldName="mainname"
                                            showSort={true}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={filters}
                                            sort={orders}
                                            searchMinIndex={1}
                                        />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} align="left">
                                        <TableHeaderContentCell
                                            textAlign="left"
                                            title="specialized_report:h.description"
                                            updateOrderList={updateOrderList}
                                            updateFilterList={updateFilterList}
                                            fieldName="description"
                                            showSort={true}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={filters}
                                            sort={orders}
                                            searchMinIndex={1}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {collections &&
                                    collections['hydra:member'].map((el: SpecializedReportTypeInterface) => {
                                        return (
                                            <TableRow key={el.id}>
                                                <TableCell classes={tableStickyHeader} align="left" />
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    {el.mainname}
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    {el.description}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell classes={tableCellRoot} colSpan={4}>
                                        {collections && collections['hydra:view'] && (
                                            <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </PaperedTable>
                        <Loader visible={visibleLoader} />
                    </>
                )}{' '}
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(SpecializedReport);
